import React from 'react';
import { ChevronLeft, Target, Info, TrendingUp, Calendar, RefreshCw } from 'lucide-react';
import { Button } from '../components/Button';
interface ProgramDetailsProps {
  onBack: () => void;
  onRebuildProgram?: () => void;
}
export function ProgramDetails({
  onBack,
  onRebuildProgram
}: ProgramDetailsProps) {
  return <div className="flex flex-col h-full w-full bg-stone-50 overflow-hidden" data-id="element-959">
      {/* Header */}
      <div className="flex items-center justify-between p-6 pt-8 bg-stone-50/90 backdrop-blur-md z-20 shrink-0" data-id="element-960">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-slate-200/50 text-slate-600 transition-colors" aria-label="Go back" data-id="element-961">
          <ChevronLeft size={28} data-id="element-962" />
        </button>
        <h1 className="text-xl font-extrabold text-slate-800" data-id="element-963">
          Current Program
        </h1>
        <div className="w-10" data-id="element-964" />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-32 space-y-5" data-id="element-965">
        {/* Section 1 — Program Overview */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100" data-id="element-966">
          <div className="flex items-center gap-2 mb-4" data-id="element-967">
            <div className="w-8 h-8 bg-pink-50 text-pink-500 rounded-xl flex items-center justify-center" data-id="element-968">
              <Target size={16} data-id="element-969" />
            </div>
            <h3 className="font-extrabold text-slate-800" data-id="element-970">Program Overview</h3>
          </div>

          <div className="space-y-0" data-id="element-971">
            <div className="flex justify-between items-center py-2.5 border-b border-slate-50" data-id="element-972">
              <span className="text-sm font-medium text-slate-500" data-id="element-973">
                Primary Goal
              </span>
              <span className="text-sm font-bold text-slate-800" data-id="element-974">
                Hypertrophy
              </span>
            </div>
            <div className="flex justify-between items-center py-2.5 border-b border-slate-50" data-id="element-975">
              <span className="text-sm font-medium text-slate-500" data-id="element-976">
                Frequency
              </span>
              <span className="text-sm font-bold text-slate-800" data-id="element-977">
                3 workouts per week
              </span>
            </div>
            <div className="flex justify-between items-start py-2.5 border-b border-slate-50" data-id="element-978">
              <span className="text-sm font-medium text-slate-500 shrink-0" data-id="element-979">
                Workout days
              </span>
              <span className="text-sm font-bold text-slate-800 text-right" data-id="element-980">
                Mon, Wed, Thu
              </span>
            </div>
            <div className="flex justify-between items-center py-2.5 border-b border-slate-50" data-id="element-981">
              <span className="text-sm font-medium text-slate-500" data-id="element-982">
                Structure
              </span>
              <span className="text-sm font-bold text-slate-800" data-id="element-983">
                Full Body
              </span>
            </div>
            <div className="flex justify-between items-center py-2.5 border-b border-slate-50" data-id="element-984">
              <span className="text-sm font-medium text-slate-500" data-id="element-985">
                Duration
              </span>
              <span className="text-sm font-bold text-slate-800" data-id="element-986">12 weeks</span>
            </div>
            <div className="flex justify-between items-center py-2.5 border-b border-slate-50" data-id="element-987">
              <span className="text-sm font-medium text-slate-500" data-id="element-988">
                Current week
              </span>
              <span className="text-sm font-bold text-emerald-600" data-id="element-989">
                Week 3 of 12
              </span>
            </div>
            <div className="flex justify-between items-center py-2.5 border-b border-slate-50" data-id="element-990">
              <span className="text-sm font-medium text-slate-500" data-id="element-991">
                Program start
              </span>
              <span className="text-sm font-bold text-slate-800" data-id="element-992">
                25 March 2026
              </span>
            </div>
            <div className="flex justify-between items-center py-2.5" data-id="element-993">
              <span className="text-sm font-medium text-slate-500" data-id="element-994">
                Last updated
              </span>
              <span className="text-sm font-bold text-slate-800" data-id="element-995">
                22 March 2026
              </span>
            </div>
          </div>
        </div>

        {/* Section 2 — How This Program Works */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100" data-id="element-996">
          <div className="flex items-center gap-2 mb-4" data-id="element-997">
            <div className="w-8 h-8 bg-blue-50 text-blue-500 rounded-xl flex items-center justify-center" data-id="element-998">
              <Info size={16} data-id="element-999" />
            </div>
            <h3 className="font-extrabold text-slate-800" data-id="element-1000">
              How This Program Works
            </h3>
          </div>

          <p className="text-sm font-medium text-slate-500 leading-relaxed mb-2" data-id="element-1001">
            Your plan is built around key movement patterns, balanced weekly
            volume, and gradual progression.
          </p>
          <p className="text-sm font-medium text-slate-500 leading-relaxed" data-id="element-1002">
            When you confirm completed workouts, the app uses your results to
            adjust future sessions over time.
          </p>
        </div>

        {/* Section 3 — Progress Snapshot */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100" data-id="element-1003">
          <div className="flex items-center gap-2 mb-4" data-id="element-1004">
            <div className="w-8 h-8 bg-emerald-50 text-emerald-500 rounded-xl flex items-center justify-center" data-id="element-1005">
              <TrendingUp size={16} data-id="element-1006" />
            </div>
            <h3 className="font-extrabold text-slate-800" data-id="element-1007">Progress Snapshot</h3>
          </div>

          <div className="space-y-0" data-id="element-1008">
            <div className="flex justify-between items-center py-2.5 border-b border-slate-50" data-id="element-1009">
              <span className="text-sm font-medium text-slate-500" data-id="element-1010">
                Workouts this week
              </span>
              <span className="text-sm font-bold text-slate-800" data-id="element-1011">1 of 3</span>
            </div>
            <div className="flex justify-between items-center py-2.5 border-b border-slate-50" data-id="element-1012">
              <span className="text-sm font-medium text-slate-500" data-id="element-1013">
                Completion rate
              </span>
              <span className="text-sm font-bold text-emerald-600" data-id="element-1014">
                81% (last 28 days)
              </span>
            </div>
            <div className="flex justify-between items-center py-2.5 border-b border-slate-50" data-id="element-1015">
              <span className="text-sm font-medium text-slate-500" data-id="element-1016">
                Last week's volume
              </span>
              <span className="text-sm font-bold text-slate-800" data-id="element-1017">
                12,460 kg
              </span>
            </div>
            <div className="flex justify-between items-center py-2.5 border-b border-slate-50" data-id="element-1018">
              <span className="text-sm font-medium text-slate-500" data-id="element-1019">
                Next workout
              </span>
              <div className="flex items-center gap-1.5" data-id="element-1020">
                <Calendar size={14} className="text-slate-400" data-id="element-1021" />
                <span className="text-sm font-bold text-slate-800" data-id="element-1022">
                  Thu, 26 March
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center py-2.5" data-id="element-1023">
              <span className="text-sm font-medium text-slate-500" data-id="element-1024">
                Plan last adjusted
              </span>
              <span className="text-sm font-bold text-slate-800" data-id="element-1025">
                22 March 2026
              </span>
            </div>
          </div>
        </div>

        {/* Section 4 — Rebuild Action */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100" data-id="element-1026">
          <p className="text-sm font-medium text-slate-500 leading-relaxed mb-4 text-center" data-id="element-1027">
            Want to refresh your training plan based on your current goals and
            schedule?
          </p>
          <Button variant="primary" fullWidth onClick={onRebuildProgram} data-id="element-1028">
            <RefreshCw size={16} className="mr-2" data-id="element-1029" />
            Rebuild program
          </Button>
        </div>
      </div>
    </div>;
}